import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { CATEGORY_COLORS, formatDate } from "../lib/categories";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, Users, User, Building2, BadgeCheck } from "lucide-react";

export default function PublicSchoolMembers() {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const [club, setClub] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get(`/public/clubs/${clubId}`),
      api.get(`/public/clubs/${clubId}/members`),
    ])
      .then(([c, m]) => { setClub(c.data); setMembers(m.data); })
      .catch(() => navigate("/"))
      .finally(() => setLoading(false));
  }, [clubId, navigate]);

  return (
    <div className="App min-h-screen bg-background">
      <Navbar />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-6 transition-colors" data-testid="back-to-clubs">
          <ArrowLeft className="w-4 h-4" /> Nazad na spisak škola
        </button>

        {loading ? (
          <div className="py-20 text-center text-muted-foreground">Učitavanje...</div>
        ) : !club ? null : (
          <>
            <div className="flex flex-wrap items-center gap-5 border-b-2 border-foreground pb-6 mb-8">
              <div className="w-[150px] h-[150px] border-2 border-primary/60 overflow-hidden bg-muted flex items-center justify-center shrink-0">
                {club.logo_path ? <img src={fileUrl(club.logo_path)} alt={club.name} className="w-full h-full object-cover" /> : <Building2 className="w-12 h-12 text-muted-foreground" />}
              </div>
              <div className="flex-1 min-w-[220px]">
                <h1 className="font-display text-4xl font-black tracking-tighter" data-testid="school-name">{club.name}</h1>
                <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {club.city}</span>
                  <span className="flex items-center gap-1.5"><Users className="w-4 h-4 text-primary" /> aktivno <strong className="text-foreground" data-testid="school-member-count">{club.member_count}</strong> izvođača</span>
                  {club.active_season && <span className="text-xs uppercase tracking-wider">Sezona {club.active_season}</span>}
                </div>
              </div>
            </div>

            {members.length === 0 ? (
              <div className="py-16 text-center text-muted-foreground border-2 border-dashed border-border" data-testid="school-no-members">
                <Users className="w-10 h-10 mx-auto mb-3 opacity-40" />
                Ova škola trenutno nema prijavljenih izvođača.
              </div>
            ) : (
              <div>
                <div className="hidden md:grid grid-cols-[50px_100px_1fr_150px_150px_120px] gap-4 px-4 py-3 text-xs uppercase tracking-[0.15em] text-white bg-[hsl(var(--secondary))]">
                  <div>Rb.</div><div>Slika</div><div>Ime i prezime</div><div>Datum rođenja</div><div>Kategorija</div><div>Licenca</div>
                </div>
                <div className="space-y-2 mt-2">
                  {members.map((m, i) => (
                    <motion.div
                      key={m.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: Math.min(i * 0.03, 0.5) }}
                      className={`grid grid-cols-[80px_1fr] md:grid-cols-[50px_100px_1fr_150px_150px_120px] gap-3 md:gap-4 px-4 py-3 items-center border-2 border-primary/70 ${m.licensed ? "bg-sky-500/15" : "bg-white/5"}`}
                      data-testid={`school-member-${m.id}`}
                    >
                      <div className="hidden md:block"><span className="inline-flex items-center justify-center min-w-[30px] px-2 py-1 bg-[hsl(var(--secondary))] text-white font-display font-bold text-sm">{String(i + 1).padStart(2, "0")}</span></div>
                      <div className="w-16 h-16 md:w-20 md:h-20 border border-border bg-muted overflow-hidden flex items-center justify-center">
                        {m.photo_path ? <img src={fileUrl(m.photo_path)} alt="" className="w-full h-full object-cover" /> : <User className="w-7 h-7 text-muted-foreground" />}
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-lg truncate">{m.last_name} {m.first_name}</div>
                        <div className="text-xs text-muted-foreground md:hidden">{formatDate(m.dob)} · {m.age_category}{m.licensed ? " · Licenca ✓" : ""}</div>
                      </div>
                      <div className="hidden md:block text-sm text-muted-foreground">{formatDate(m.dob)} <span className="text-xs">({m.age})</span></div>
                      <div className="hidden md:block">
                        <span className={`inline-block text-xs font-semibold px-2 py-1 border ${CATEGORY_COLORS[m.age_category] || CATEGORY_COLORS["Van kategorije"]}`}>{m.age_category}</span>
                      </div>
                      <div className="hidden md:block">
                        {m.licensed ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-sky-400"><BadgeCheck className="w-4 h-4" /> Licenciran</span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
